/**
 * Settings Page
 * System configuration and user preferences
 */

import React, { useState, useEffect } from 'react';
import { Card } from '@components/common/card';
import { Button } from '@components/common/button';
import { Input } from '@components/common/input';
import { toast } from 'react-toastify';
import {
  Building,
  Mail,
  Phone,
  MapPin,
  DollarSign,
  Percent,
  User,
  Lock,
  Bell,
  Database,
  Settings as SettingsIcon,
  Save,
  AlertCircle,
  Loader2
} from 'lucide-react';
import {
  settings_service,
  CompanySettings,
  BillingSettings,
  NotificationSettings,
  AccountSettings,
  SystemInfo,
  PasswordChangeData
} from '@services/settings_service';

export const SettingsPage: React.FC = () => {
  const [is_loading, set_is_loading] = useState(true);
  const [is_saving, set_is_saving] = useState(false);
  const [active_tab, set_active_tab] = useState<'company' | 'billing' | 'notifications' | 'account' | 'system'>('company');

  // Company Settings
  const [company_settings, set_company_settings] = useState<CompanySettings>({
    company_name: '',
    company_email: '',
    company_phone: '',
    company_address: '',
    tax_id: '',
    bank_name: '',
    account_name: '',
    account_number: ''
  });

  // Billing Settings
  const [billing_settings, set_billing_settings] = useState<BillingSettings>({
    default_tax_rate: 16,
    payment_terms: 'Payment is due within 30 days',
    invoice_prefix: 'INV',
    late_fee_enabled: false,
    late_fee_percentage: 2
  });

  // Notification Settings
  const [notification_settings, set_notification_settings] = useState<NotificationSettings>({
    email_notifications: true,
    sms_notifications: false,
    overdue_reminders: true,
    payment_confirmations: true
  });

  // Account Settings
  const [account_settings, set_account_settings] = useState<AccountSettings>({
    full_name: '',
    email: '',
    phone: ''
  });

  // System Info
  const [system_info, set_system_info] = useState<SystemInfo>({
    version: '',
    database: '',
    last_backup: '',
    storage_used: '',
    storage_total: ''
  });

  // Password Change
  const [password_data, set_password_data] = useState<PasswordChangeData>({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  // Load settings on mount
  useEffect(() => {
    load_all_settings();
  }, []);

  /**
   * Load all settings from backend
   */
  const load_all_settings = async () => {
    try {
      set_is_loading(true);
      const data = await settings_service.get_all_settings();
      
      set_company_settings(data.company);
      set_billing_settings(data.billing);
      set_notification_settings(data.notifications);
      set_account_settings(data.account);
      set_system_info(data.system);
    } catch (error: any) {
      console.error('Failed to load settings:', error);
      toast.error(error.message || 'Failed to load settings');
    } finally {
      set_is_loading(false);
    }
  };

  /**
   * Save current tab settings
   */
  const handle_save = async () => {
    try {
      set_is_saving(true);

      switch (active_tab) {
        case 'company':
          // Validate company settings
          const company_errors = settings_service.validate_company_settings(company_settings);
          if (company_errors.length > 0) {
            toast.error(company_errors[0]);
            return;
          }
          
          await settings_service.update_company_settings(company_settings);
          toast.success('Company settings saved successfully');
          break;

        case 'billing':
          // Validate billing settings
          const billing_errors = settings_service.validate_billing_settings(billing_settings);
          if (billing_errors.length > 0) {
            toast.error(billing_errors[0]);
            return;
          }
          
          await settings_service.update_billing_settings(billing_settings);
          toast.success('Billing settings saved successfully');
          break;

        case 'notifications':
          await settings_service.update_notification_settings(notification_settings);
          toast.success('Notification settings saved successfully');
          break;

        case 'account':
          await settings_service.update_account_settings(account_settings);
          toast.success('Account settings saved successfully');
          break;

        case 'system':
          toast.info('System settings are read-only');
          break;
      }
    } catch (error: any) {
      console.error('Error saving settings:', error);
      toast.error(error.message || 'Failed to save settings');
    } finally {
      set_is_saving(false);
    }
  };

  /**
   * Handle password change
   */
  const handle_password_change = async () => {
    try {
      // Validate password data
      const errors = settings_service.validate_password_change(password_data);
      if (errors.length > 0) {
        toast.error(errors[0]);
        return;
      }

      set_is_saving(true);
      await settings_service.change_password(password_data);
      
      // Clear password fields
      set_password_data({
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
      
      toast.success('Password changed successfully');
    } catch (error: any) {
      console.error('Error changing password:', error);
      toast.error(error.message || 'Failed to change password');
    } finally {
      set_is_saving(false);
    }
  };

  /**
   * Handle database backup
   */
  const handle_backup = async () => {
    try {
      set_is_saving(true);
      const result = await settings_service.backup_database();
      toast.success(result.message || 'Database backed up successfully');
    } catch (error: any) {
      console.error('Error backing up database:', error);
      toast.error(error.message || 'Failed to backup database');
    } finally {
      set_is_saving(false);
    }
  };

  /**
   * Handle cache clear
   */
  const handle_clear_cache = async () => {
    try {
      set_is_saving(true);
      const result = await settings_service.clear_cache();
      toast.success(result.message || 'Cache cleared successfully');
    } catch (error: any) {
      console.error('Error clearing cache:', error);
      toast.error(error.message || 'Failed to clear cache');
    } finally {
      set_is_saving(false);
    }
  };

  /**
   * Handle system reset
   */
  const handle_reset_system = async () => {
    const confirmation = window.prompt(
      'This will reset all system data. Type "RESET" to confirm:'
    );

    if (!confirmation) return;

    try {
      set_is_saving(true);
      await settings_service.reset_system(confirmation);
      toast.success('System reset successfully');
      
      // Reload page after reset
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    } catch (error: any) {
      console.error('Error resetting system:', error);
      toast.error(error.message || 'Failed to reset system');
    } finally {
      set_is_saving(false);
    }
  };

  const tabs = [
    { id: 'company', label: 'Company Info', icon: <Building size={18} /> },
    { id: 'billing', label: 'Billing', icon: <DollarSign size={18} /> },
    { id: 'notifications', label: 'Notifications', icon: <Bell size={18} /> },
    { id: 'account', label: 'Account', icon: <User size={18} /> },
    { id: 'system', label: 'System', icon: <SettingsIcon size={18} /> }
  ];

  if (is_loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Loader2 size={48} className="animate-spin text-forest-green mx-auto mb-4" />
          <p className="text-gray-600">Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-charcoal">Settings</h1>
          <p className="text-gray-600 mt-1">
            Manage system configuration and preferences
          </p>
        </div>
        {active_tab !== 'system' && (
          <Button
            variant="primary"
            onClick={handle_save}
            is_loading={is_saving}
            icon_left={<Save size={18} />}
          >
            Save Changes
          </Button>
        )}
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <div className="flex gap-2 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => set_active_tab(tab.id as any)}
              className={`flex items-center gap-2 px-4 py-3 font-medium transition-smooth ${
                active_tab === tab.id
                  ? 'text-forest-green border-b-2 border-forest-green'
                  : 'text-gray-500 hover:text-charcoal'
              }`}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Company Info Tab */}
      {active_tab === 'company' && (
        <div className="space-y-6">
          <Card title="Company Information" padding="md">
            <div className="space-y-4">
              <Input
                label="Company Name"
                value={company_settings.company_name}
                onChange={(e) => set_company_settings({...company_settings, company_name: e.target.value})}
                icon_left={<Building size={18} />}
                is_required
              />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Email Address"
                  type="email"
                  value={company_settings.company_email}
                  onChange={(e) => set_company_settings({...company_settings, company_email: e.target.value})}
                  icon_left={<Mail size={18} />}
                  is_required
                />
                <Input
                  label="Phone Number"
                  type="tel"
                  value={company_settings.company_phone}
                  onChange={(e) => set_company_settings({...company_settings, company_phone: e.target.value})}
                  icon_left={<Phone size={18} />}
                  is_required
                />
              </div>
              <Input
                label="Physical Address"
                value={company_settings.company_address}
                onChange={(e) => set_company_settings({...company_settings, company_address: e.target.value})}
                icon_left={<MapPin size={18} />}
                is_required
              />
              <Input
                label="Tax ID / PIN"
                value={company_settings.tax_id}
                onChange={(e) => set_company_settings({...company_settings, tax_id: e.target.value})}
                icon_left={<DollarSign size={18} />}
                helper_text="Your business tax identification number"
              />
            </div>
          </Card>

          <Card title="Bank Details" padding="md">
            <div className="space-y-4">
              <Input
                label="Bank Name"
                value={company_settings.bank_name || ''}
                onChange={(e) => set_company_settings({...company_settings, bank_name: e.target.value})}
                placeholder="e.g., Kenya Commercial Bank"
                icon_left={<Building size={18} />}
              />
              <Input
                label="Account Name"
                value={company_settings.account_name || ''}
                onChange={(e) => set_company_settings({...company_settings, account_name: e.target.value})}
                placeholder="Account holder name"
                icon_left={<User size={18} />}
              />
              <Input
                label="Account Number"
                value={company_settings.account_number || ''}
                onChange={(e) => set_company_settings({...company_settings, account_number: e.target.value})}
                placeholder="1234567890"
                icon_left={<DollarSign size={18} />}
              />
            </div>
          </Card>
        </div>
      )}

      {/* Billing Tab */}
      {active_tab === 'billing' && (
        <div className="space-y-6">
          <Card title="Invoice Settings" padding="md">
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Default Tax Rate (%)"
                  type="number"
                  step="0.01"
                  value={String(billing_settings.default_tax_rate)}
                  onChange={(e) => set_billing_settings({...billing_settings, default_tax_rate: parseFloat(e.target.value) || 0})}
                  icon_left={<Percent size={18} />}
                  helper_text="Default VAT rate for invoices"
                />
                <Input
                  label="Invoice Number Prefix"
                  value={billing_settings.invoice_prefix}
                  onChange={(e) => set_billing_settings({...billing_settings, invoice_prefix: e.target.value})}
                  placeholder="INV"
                  helper_text="Prefix for invoice numbers"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-charcoal mb-1.5">
                  Payment Terms
                </label>
                <textarea
                  value={billing_settings.payment_terms}
                  onChange={(e) => set_billing_settings({...billing_settings, payment_terms: e.target.value})}
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-forest-green focus:border-forest-green transition-smooth resize-none"
                />
              </div>
            </div>
          </Card>

          <Card title="Late Fees" padding="md">
            <div className="space-y-4">
              <label className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-smooth">
                <input
                  type="checkbox"
                  checked={billing_settings.late_fee_enabled}
                  onChange={(e) => set_billing_settings({...billing_settings, late_fee_enabled: e.target.checked})}
                  className="w-5 h-5 text-forest-green border-gray-300 rounded focus:ring-forest-green"
                />
                <div>
                  <p className="font-medium text-charcoal">Enable Late Fees</p>
                  <p className="text-xs text-gray-500">
                    Automatically apply late fees to overdue invoices
                  </p>
                </div>
              </label>

              {billing_settings.late_fee_enabled && (
                <Input
                  label="Late Fee Percentage (%)"
                  type="number"
                  step="0.1"
                  value={String(billing_settings.late_fee_percentage)}
                  onChange={(e) => set_billing_settings({...billing_settings, late_fee_percentage: parseFloat(e.target.value) || 0})}
                  icon_left={<Percent size={18} />}
                  helper_text="Percentage applied per month overdue"
                />
              )}
            </div>
          </Card>
        </div>
      )}

      {/* Notifications Tab */}
      {active_tab === 'notifications' && (
        <Card title="Notification Preferences" padding="md">
          <div className="space-y-4">
            <label className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-smooth">
              <input
                type="checkbox"
                checked={notification_settings.email_notifications}
                onChange={(e) => set_notification_settings({...notification_settings, email_notifications: e.target.checked})}
                className="w-5 h-5 text-forest-green border-gray-300 rounded focus:ring-forest-green"
              />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <Mail size={18} className="text-forest-green" />
                  <p className="font-medium text-charcoal">Email Notifications</p>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Receive notifications via email
                </p>
              </div>
            </label>

            <label className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-smooth">
              <input
                type="checkbox"
                checked={notification_settings.sms_notifications}
                onChange={(e) => set_notification_settings({...notification_settings, sms_notifications: e.target.checked})}
                className="w-5 h-5 text-forest-green border-gray-300 rounded focus:ring-forest-green"
              />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <Phone size={18} className="text-forest-green" />
                  <p className="font-medium text-charcoal">SMS Notifications</p>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Receive notifications via SMS (charges may apply)
                </p>
              </div>
            </label>

            <label className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-smooth">
              <input
                type="checkbox"
                checked={notification_settings.overdue_reminders}
                onChange={(e) => set_notification_settings({...notification_settings, overdue_reminders: e.target.checked})}
                className="w-5 h-5 text-forest-green border-gray-300 rounded focus:ring-forest-green"
              />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <AlertCircle size={18} className="text-warning" />
                  <p className="font-medium text-charcoal">Overdue Payment Reminders</p>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Send automatic reminders for overdue invoices
                </p>
              </div>
            </label>

            <label className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-smooth">
              <input
                type="checkbox"
                checked={notification_settings.payment_confirmations}
                onChange={(e) => set_notification_settings({...notification_settings, payment_confirmations: e.target.checked})}
                className="w-5 h-5 text-forest-green border-gray-300 rounded focus:ring-forest-green"
              />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <DollarSign size={18} className="text-success" />
                  <p className="font-medium text-charcoal">Payment Confirmations</p>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Send payment receipts automatically
                </p>
              </div>
            </label>
          </div>
        </Card>
      )}

      {/* Account Tab */}
      {active_tab === 'account' && (
        <div className="space-y-6">
          <Card title="Profile Information" padding="md">
            <div className="space-y-4">
              <Input
                label="Full Name"
                value={account_settings.full_name}
                onChange={(e) => set_account_settings({...account_settings, full_name: e.target.value})}
                icon_left={<User size={18} />}
              />
              <Input
                label="Email Address"
                type="email"
                value={account_settings.email}
                onChange={(e) => set_account_settings({...account_settings, email: e.target.value})}
                icon_left={<Mail size={18} />}
              />
              <Input
                label="Phone Number"
                type="tel"
                value={account_settings.phone}
                onChange={(e) => set_account_settings({...account_settings, phone: e.target.value})}
                icon_left={<Phone size={18} />}
              />
            </div>
          </Card>

          <Card title="Change Password" padding="md">
            <div className="space-y-4">
              <Input
                label="Current Password"
                type="password"
                value={password_data.current_password}
                onChange={(e) => set_password_data({...password_data, current_password: e.target.value})}
                icon_left={<Lock size={18} />}
              />
              <Input
                label="New Password"
                type="password"
                value={password_data.new_password}
                onChange={(e) => set_password_data({...password_data, new_password: e.target.value})}
                icon_left={<Lock size={18} />}
              />
              <Input
                label="Confirm New Password"
                type="password"
                value={password_data.confirm_password}
                onChange={(e) => set_password_data({...password_data, confirm_password: e.target.value})}
                icon_left={<Lock size={18} />}
              />
              <Button 
                variant="primary"
                onClick={handle_password_change}
                is_loading={is_saving}
              >
                Update Password
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* System Tab */}
      {active_tab === 'system' && (
        <div className="space-y-6">
          <Card title="System Information" padding="md">
            <div className="space-y-3 text-sm">
              <div className="flex justify-between py-2 border-b border-gray-200">
                <span className="text-gray-600">Version:</span>
                <span className="font-semibold text-charcoal">{system_info.version}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-gray-200">
                <span className="text-gray-600">Database:</span>
                <span className="font-semibold text-charcoal">{system_info.database}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-gray-200">
                <span className="text-gray-600">Last Backup:</span>
                <span className="font-semibold text-charcoal">{system_info.last_backup}</span>
              </div>
              <div className="flex justify-between py-2">
                <span className="text-gray-600">Storage Used:</span>
                <span className="font-semibold text-charcoal">
                  {system_info.storage_used} / {system_info.storage_total}
                </span>
              </div>
            </div>
          </Card>

          <Card title="Maintenance" padding="md">
            <div className="space-y-3">
              <Button 
                variant="outline" 
                full_width 
                icon_left={<Database size={18} />}
                onClick={handle_backup}
                is_loading={is_saving}
              >
                Backup Database
              </Button>
              <Button 
                variant="outline" 
                full_width 
                icon_left={<SettingsIcon size={18} />}
                onClick={handle_clear_cache}
                is_loading={is_saving}
              >
                Clear Cache
              </Button>
              <Button 
                variant="danger" 
                full_width
                onClick={handle_reset_system}
                is_loading={is_saving}
              >
                Reset System (Admin Only)
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};